import Vue from 'vue';
import Router from 'vue-router';
import { pick, isEqual } from 'lodash';
import qs from 'query-string';
import stringInterop from '@tencent/ui-core/lib/utils/stringInterop';

import runtimeMode from '@/router/runtimeMode';
import { injectRouterMethods, routeChangeIsReplace } from '@/router/injectRouter';
import { isHmr, isLayoutHmr, getHmrPort } from '@utils/comps-loader';
import layoutLoader from '@loaders/layout/loader';
import pageLoader from '@loaders/page/loader';
import globalsLoader from '@/loaders/globals/loader';
import { executeSwitchHook } from '@/utils/executeSwitchHook';
import { getUserInfo } from '@utils/userInfo';
import { cacheRequestCollectionCgi, requestCollectionCgi } from '@utils/common';
import { wEmitter, EventName } from '@utils/global/event-emitter';
import store from '@store';
import { isRegexPath, getPathCompiler, getWithoutNilQuery } from '@/utils/path';
import logger from '@/utils/logger';

Vue.use(Router);
injectRouterMethods();

const guard = async (from, to) => {
  const { projectId, env } = window.RUNTIME_CONFIGS;
  const projectInfo = window.xy_runtime_project;
  const { enabledGuard = false, routeGuard = '' } = projectInfo?.advanceConfig ?? {};
  let route = '';
  if (enabledGuard && routeGuard) {
    route = await executeSwitchHook(routeGuard, {
      ctx: {
        projectId,
        env,
        user: getUserInfo(),
        sdk: {
          requestCollectionCgi: requestCollectionCgi.bind(undefined, projectId, env),
          cacheRequestCollectionCgi: cacheRequestCollectionCgi.bind(undefined, projectId, env),
        },
      },
      from,
      to,
    });
  }
  return route;
};

export default async ({ projectId, env, branch, aiAdvanceConfig }) => {
  if (isHmr()) {
    const port = getHmrPort();
    pageLoader.initHmr(port);
    globalsLoader.initHmr(port);
  }

  // 布局组件本地开发 HMR
  if (isLayoutHmr()) {
    const port = getHmrPort();
    layoutLoader.initHmr(port);
  }

  await globalsLoader.loadResources(projectId);

  const router = new Router(await runtimeMode({ env, projectId, branch, aiAdvanceConfig }));
  window.xy_runtime_router = router;

  let previousRoute = '';
  // to hijack
  router.beforeEach(async (to, from, next) => {
    wEmitter.emit(EventName.ROUTER_BEFORE_EACH, ({ to, from }));
    if (!previousRoute) {
      const route = await guard(from, to);
      if (route) {
        if (decodeURIComponent(route) !== decodeURIComponent(from.fullPath)) previousRoute = route;
        next(route);
        return;
      }
    } else {
      // 重置
      previousRoute = '';
      // 直接确认跳转
      next();
      return;
    }

    const commonKeys = Vue.prototype.$commonQuery ?? [];
    const layoutKeys = ['iframe', '_header', '_sidebar'];
    // 预览调试桥参数：wujiAiPageUK / uk--XXXX 是与编辑器通信的唯一标识，配合 wsUrl/browserId。
    // full-reload 后 iframe 需靠这些参数重新握手，导航时必须透传，否则丢失即断连。
    const debugBridgeKeys = [
      'wujiAiPageUK',
      'wsUrl',
      'browserId',
      ...Object.keys(from.query).filter(key => key.startsWith('uk--')),
    ];
    const fromQuery = pick(from.query, [...commonKeys, ...layoutKeys, ...debugBridgeKeys]);
    const query = { ...fromQuery, ...to.query };

    // 兼容首页是正则表达式路径的情况，其余情况由 vue-router.redirect 处理
    if (to?.fullPath === '/') {
      try {
        const project = await store.dispatch('runtime/getProject', { projectId, env });
        const homePath = project?.homePage?.path;
        if (isRegexPath(homePath)) {
          const compiler = getPathCompiler(homePath);
          const compiledPath = compiler(from?.params ?? {}, { pretty: true });
          const mergedQuery = {
            ...query,
            ...qs.parse(stringInterop(project.homePage.query || '', {
              ...window?.w?.mainRenderer ?? {},
              $app: Vue.prototype.$app,
              w: window?.w,
            })),
          };
          next({ path: compiledPath, query: getWithoutNilQuery(mergedQuery) });
          return;
        }
      } catch (err) {
        // 忽略抛错，交给 vue-router.redirect 处理
        logger.error(err);
      }
    }

    if (isEqual(query, to.query)) next();
    else next({ ...to, query, replace: routeChangeIsReplace() });
  });

  router.afterEach((to, from) => {
    wEmitter.emit(EventName.ROUTER_AFTER_EACH, ({ to, from }));
  });

  router.afterEach(() => {
    performance.mark('ROUTER_AFTER');
    if (router.TOP_SWITCHING) history.go();
  });


  return router;
};
